import type { AdminReportListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportsFilterBar } from './_components/ReportsFilterBar';
import { buildReportColumns } from './_lib/reportColumns';
import { hasActiveFilters, reportFiltersSchema } from './_lib/reportFilters';
import { ReportDetailDrawer } from './ReportDetailDrawer';
import { useAdminReportsList } from '../../api/admin-reports';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/reports` — admin reports moderation list (AUT-127 / ADMIN-15).
 *
 * Gated on `moderate_reports` (the controller-level permission). PII
 * columns (inspector name) render via `<RedactedField perm="view_pii">`,
 * which agrees with the server's `piiRevealed` flag.
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * `useCursorPagination`. Row click opens the 720px detail drawer.
 *
 * Clones `InspectorsListPage` / `OrdersListPage` shape — same primitive
 * stack, same observer wrap, same filter-reset-on-change effect.
 */
const ReportsListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(reportFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${filters.inspectorId ?? ''}|${
    filters.dateFrom ?? ''
  }|${filters.dateTo ?? ''}|${filters.q ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminReportsList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.inspectorId ? { inspectorId: filters.inspectorId } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildReportColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('reports.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('reports.subtitle')}
        </Typography>
      </Box>

      <ReportsFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminReportListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('reports.empty')}
          </Typography>
        }
      />

      <ReportDetailDrawer reportId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const Observed = observer(ReportsListPageInner);

/** Whole-page gate: no `moderate_reports` → the default 403 panel. */
export const ReportsListPage = (): ReactElement => (
  <RequirePermission perm="moderate_reports">
    <Observed />
  </RequirePermission>
);
